import type { LucideIcon } from "lucide-react";
import {
  Users,
  FolderKanban,
  Receipt,
  TrendingUp,
  Truck,
  Calculator,
  BarChart3,
  PieChart,
  LineChart,
  UserCog,
  Settings,
  Upload,
} from "lucide-react";

export interface RouteConfig {
  title: string;
  breadcrumbLabel: string;
  icon?: LucideIcon;
  parentPath?: string;
}

export const routeConfigs: Record<string, RouteConfig> = {
  // Analytics pages
  "/": {
    title: "Analityka Przychodów",
    breadcrumbLabel: "Przychody",
    icon: BarChart3,
  },
  "/analytics/projects": {
    title: "Analityka Projektów",
    breadcrumbLabel: "Wg projektów",
    icon: PieChart,
    parentPath: "/",
  },
  "/analytics/clients": {
    title: "Analityka Klientów",
    breadcrumbLabel: "Wg klientów",
    icon: LineChart,
    parentPath: "/",
  },

  // Data pages
  "/clients": {
    title: "Klienci",
    breadcrumbLabel: "Klienci",
    icon: Users,
  },
  "/projects": {
    title: "Projekty",
    breadcrumbLabel: "Projekty",
    icon: FolderKanban,
  },
  "/costs": {
    title: "Koszty",
    breadcrumbLabel: "Koszty",
    icon: Receipt,
  },
  "/revenues": {
    title: "Przychody",
    breadcrumbLabel: "Przychody",
    icon: TrendingUp,
  },
  "/suppliers": {
    title: "Dostawcy",
    breadcrumbLabel: "Dostawcy",
    icon: Truck,
  },

  // Import wizards
  "/import-clients": {
    title: "Import Klientów",
    breadcrumbLabel: "Import CSV",
    icon: Upload,
    parentPath: "/clients",
  },
  "/import-projects": {
    title: "Import Projektów",
    breadcrumbLabel: "Import CSV",
    icon: Upload,
    parentPath: "/projects",
  },
  "/import-costs": {
    title: "Import Kosztów",
    breadcrumbLabel: "Import CSV",
    icon: Upload,
    parentPath: "/costs",
  },
  "/import-revenues": {
    title: "Import Przychodów",
    breadcrumbLabel: "Import CSV",
    icon: Upload,
    parentPath: "/revenues",
  },
  "/import-suppliers": {
    title: "Import Dostawców",
    breadcrumbLabel: "Import CSV",
    icon: Upload,
    parentPath: "/suppliers",
  },

  // Management
  "/management/clients": {
    title: "Zarządzanie Klientami",
    breadcrumbLabel: "Zarządzanie",
    icon: UserCog,
  },
  // Dynamic route - will be matched with pattern
  "/management/clients/:nrPorzadkowy": {
    title: "Profil Klienta",
    breadcrumbLabel: ":nrPorzadkowy",
    parentPath: "/management/clients",
  },

  // Settings
  "/settings": {
    title: "Ustawienia",
    breadcrumbLabel: "Ustawienia",
    icon: Settings,
  },
  "/settings/:section": {
    title: "Ustawienia",
    breadcrumbLabel: ":section",
    parentPath: "/settings",
  },
};

/**
 * Match a pathname to a route config, handling dynamic segments like :nrPorzadkowy
 */
export function getRouteConfig(pathname: string): RouteConfig | null {
  // First try exact match
  if (routeConfigs[pathname]) {
    return routeConfigs[pathname];
  }

  // Try pattern matching for dynamic routes
  for (const [pattern, config] of Object.entries(routeConfigs)) {
    if (!pattern.includes(":")) continue;

    const patternParts = pattern.split("/");
    const pathParts = pathname.split("/");

    if (patternParts.length !== pathParts.length) continue;

    let matches = true;
    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i].startsWith(":")) continue; // Dynamic segment, always matches
      if (patternParts[i] !== pathParts[i]) {
        matches = false;
        break;
      }
    }

    if (matches) {
      return config;
    }
  }

  return null;
}

export interface BreadcrumbItem {
  label: string;
  path: string;
  isCurrentPage: boolean;
}

/**
 * Build breadcrumb items for a given pathname
 */
export function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [];
  let currentPath = pathname;

  // Build breadcrumb chain by following parentPath
  while (currentPath) {
    const config = getRouteConfig(currentPath);
    if (!config) break;

    // Resolve dynamic breadcrumb labels
    let label = config.breadcrumbLabel;
    if (label.startsWith(":")) {
      // Extract the actual value from the pathname
      const paramName = label.slice(1); // Remove the ":"
      const pathParts = currentPath.split("/");

      // Find the corresponding pattern to get the index
      for (const [pattern] of Object.entries(routeConfigs)) {
        if (!pattern.includes(":")) continue;
        const patternParts = pattern.split("/");
        if (patternParts.length !== pathParts.length) continue;

        let matches = true;
        let paramIndex = -1;
        for (let i = 0; i < patternParts.length; i++) {
          if (patternParts[i] === `:${paramName}`) {
            paramIndex = i;
          } else if (patternParts[i].startsWith(":")) {
            continue;
          } else if (patternParts[i] !== pathParts[i]) {
            matches = false;
            break;
          }
        }

        if (matches && paramIndex !== -1) {
          label = pathParts[paramIndex];
          break;
        }
      }
    }

    items.unshift({
      label,
      path: currentPath,
      isCurrentPage: currentPath === pathname,
    });

    currentPath = config.parentPath || "";
  }

  return items;
}
